'use client';

import React, { useMemo } from 'react';
import { faBookmark } from '@fortawesome/pro-solid-svg-icons';
import { useGetStrategyMetricsQuery } from 'src/api/redux';
import { useGetEngageStrategyConnectorQuery } from 'src/api/redux/strategy/engageStrategyApi';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { StrategyMapHeaderMetricBlock } from '../StrategyMapHeaderMetricBlock';

interface DisplayOption {
  code: string;
  type: string;
  label: string;
}

interface StrategyMetric {
  id: number;
  label: string;
  display_options: DisplayOption[];
}

interface StrategyMetricsResponse {
  results: StrategyMetric[];
}

interface EngageStrategyMetricsHeaderProps {
  engageStrategyId: string;
  color: string;
  compact?: boolean;
}

const EngageStrategyMetricsHeader: React.FC<
  EngageStrategyMetricsHeaderProps
> = ({ engageStrategyId, color, compact = false }) => {
  const { entityDetails } = useProfile();

  const { data: strategyMetrics } = useGetStrategyMetricsQuery(
    {
      strategyType: 'engage',
      strategyId: engageStrategyId,
      scope: 'OVERVIEW',
    },
    {
      skip: !engageStrategyId,
    }
  );

  const { providerId: rawProviderId } = getEntityIdentifiers(entityDetails);
  const providerId = rawProviderId?.toString() || '';

  const { data: connector } = useGetEngageStrategyConnectorQuery(
    {
      strategyId: engageStrategyId,
      providerId,
    },
    { skip: !engageStrategyId || !providerId }
  );

  const metricBlocks = useMemo(() => {
    const metricsResponse = strategyMetrics as
      | StrategyMetricsResponse
      | undefined;

    if (!metricsResponse?.results || !connector?.metrics) {
      return [];
    }

    const blocks: Array<{
      id: string;
      title: string;
      date: string;
      value: string;
      icon: typeof faBookmark;
    }> = [];

    metricsResponse.results.forEach((metric) => {
      const metricValues = connector.metrics[metric.id];

      if (metricValues === undefined || metricValues === null) return;

      // Only show the first display option for now (toggle coming later)
      const option = metric.display_options?.[0];
      if (!option) return;

      // Handle both formats: simple (number) or nested (object keyed by option.code)
      const rawValue =
        typeof metricValues === 'object'
          ? metricValues[option.code]
          : metricValues;

      if (rawValue === undefined) return;

      let formattedValue: string;
      if (metric.label === 'Capture Rate') {
        formattedValue = `${Math.round(rawValue * 100)}%`;
      } else {
        formattedValue = rawValue.toLocaleString();
      }

      blocks.push({
        id: `${metric.id}-${option.code}`,
        title: metric.label,
        date: '',
        value: formattedValue,
        icon: faBookmark,
      });
    });

    return blocks;
  }, [strategyMetrics, connector]);

  if (metricBlocks.length === 0) {
    return null;
  }

  return (
    <div className={`self-stretch grid ${compact ? 'grid-cols-2 gap-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'}`}>
      {metricBlocks.map((item) => (
        <StrategyMapHeaderMetricBlock key={item.id} item={item} color={color} compact={compact} />
      ))}
    </div>
  );
};

export default EngageStrategyMetricsHeader;
